import qs from 'qs';
import { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { BannerStyle } from '../../../../typings/enums';
import { useOnConfirm } from '../../../contexts/DialogContext';
import { window } from '../../../globals';
import { formatDateWithTimeZone } from '../../../helpers/date';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useCancelOrgDeletion } from '../../../hooks/api/internalTools/orgDeletion/useCancelOrgDeletion';
import { useGetOrgDeletions } from '../../../hooks/api/internalTools/orgDeletion/useGetOrgDeletions';
import UserStore from '../../../redux/users';
import Banner from '../../generic/Banner';
import GenericButton from '../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button/button-constants';
import { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link/Link';
import Tooltip from '../../generic/Tooltip';
import { ORG_DELETION } from '../../../../shared/featureFlags/flagNames';
import { OPEN_ORG_DELETION_STATUSES } from '../../../../shared/InternalTools/orgDeletion';
import type { InternalToolsOrg, OrgRequestParams } from '../../../../shared/InternalTools/orgs';
import type { User } from '../../../types';

import { OrgDeletionDialog } from './OrgDeletionDialog';

type OrganizationActionsProps = {
  selectedOrg: InternalToolsOrg;
  orgRequestParams: OrgRequestParams;
};

const OrganizationActions: VoidFunctionComponent<OrganizationActionsProps> = ({
  selectedOrg,
  orgRequestParams,
}) => {
  const dispatch = useDispatch();
  const isOrgDeletionEnabled = useIsFeatureEnabled(ORG_DELETION);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // The server refuses this too; disabling Delete says why before the dialog opens.
  const isSwitchedIntoOrg = selectedOrg.id === window.user?.organizationId;
  // The newest deletion decides the card's state; nothing is sent while the flag is off.
  const deletionsQuery = useGetOrgDeletions({ organizationId: selectedOrg.id, pageSize: 1 }, isOrgDeletionEnabled);
  const newestDeletion = deletionsQuery.data?.results[0] ?? null;
  const cancelMutation = useCancelOrgDeletion();
  const onConfirm = useOnConfirm();

  const onChangeToOrg = useCallback(async () => {
    if (window.user) {
      dispatch(UserStore.actions.update({
        id: window.user.id,
        organizationId: selectedOrg.id,
      } as User));
      window.location.reload();
    }
  }, [dispatch, selectedOrg.id]);

  const onCancelDeletion = (deletionId: number, scheduledFor: string) => {
    onConfirm({
      title: 'Cancel this organization deletion?',
      body: `${selectedOrg.title} will no longer be deleted on ${formatDateWithTimeZone(scheduledFor)}.`,
      confirmText: 'Cancel Deletion',
      cancelText: 'Keep Deletion',
      onConfirm: () => cancelMutation.mutate(deletionId),
    });
  };

  const renderDeleteButton = () => (
    <Tooltip
      id="org-deletion-switched-into-tooltip"
      tooltip="You can't delete the organization you're switched into"
      showTooltip={isSwitchedIntoOrg}
    >
      <span>
        <Button
          bsStyle="danger"
          disabled={isSwitchedIntoOrg || deletionsQuery.isLoading || deletionsQuery.isError}
          onClick={() => setIsDeleteDialogOpen(true)}
        >
          Delete
        </Button>
      </span>
    </Tooltip>
  );

  const renderOrgDeletionStatus = () => {
    if (newestDeletion?.status === 'SCHEDULED') {
      const queuedNote = newestDeletion.isQueued ? ' (waiting in the job queue)' : '';
      return (
        <Banner
          title={`Deletion scheduled for ${formatDateWithTimeZone(newestDeletion.scheduledFor)}${queuedNote}`}
          body=""
          bannerStyle={BannerStyle.Medium}
          actions={
            <GenericButton
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MICRO}
              isDisabled={cancelMutation.isPending}
              onClick={() => onCancelDeletion(newestDeletion.id, newestDeletion.scheduledFor)}
            >
              Cancel Deletion
            </GenericButton>
          }
        />
      );
    }
    if (newestDeletion?.status === 'IN_PROGRESS') {
      return (
        <Banner
          title={newestDeletion.startedAt
            ? `Deletion running since ${formatDateWithTimeZone(newestDeletion.startedAt)}`
            : 'Deletion running'}
          body=""
          bannerStyle={BannerStyle.Info}
        />
      );
    }
    if (deletionsQuery.isError) {
      return (
        <Banner
          title="Couldn't load this organization's deletion status."
          body=""
          bannerStyle={BannerStyle.High}
        />
      );
    }
    return null;
  };

  // A scheduled or running deletion replaces Delete; its status shows in a banner under the buttons.
  const isDeletionOpen = newestDeletion !== null && OPEN_ORG_DELETION_STATUSES.includes(newestDeletion.status);
  const orgDeletionStatus = isOrgDeletionEnabled ? renderOrgDeletionStatus() : null;

  return (
    <div className="internal-tool-component">
      <h2 className="card-title">Actions</h2>
      <div className="wrapped-row">
        <Link
          to={`/api/internal-tools/orgs.csv?${qs.stringify(orgRequestParams)}`}
          className="btn btn-default"
          openInNewTab
        >
          <IconText text="Download as CSV" icon={icons.download} />
        </Link>
        <Button onClick={onChangeToOrg}>
          Change App to {selectedOrg.title}
        </Button>
        {isOrgDeletionEnabled ? !isDeletionOpen && renderDeleteButton() : (
          <Tooltip
            tooltip="If you need to delete an organization, please submit a request to engineering"
          >
            <span>
              <Button bsStyle="danger" disabled>
                Delete
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
      {orgDeletionStatus && <div className="margin-top-md">{orgDeletionStatus}</div>}
      {isOrgDeletionEnabled && (
        <OrgDeletionDialog
          org={selectedOrg}
          show={isDeleteDialogOpen}
          onHide={() => setIsDeleteDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default OrganizationActions;
